// src/features/crypto-ranking/api.ts
//
// Data fetcher for the Crypto ranking page. Sibling of
// features/commodities-ranking/api.ts. Talks to the FastAPI
// crypto-ranking router via the shared `apiClient` axios instance, which:
//   - attaches the WordPress-issued JWT (Authorization: Bearer <token>)
//     so the auth-gated /api/v1/crypto-ranking router is reached as an
//     authorized user, and
//   - already bakes the `/api/v1` version prefix into its baseURL.
// We therefore request the bare `/crypto-ranking/...` path here — adding
// `/api/v1` would double the prefix. See api/client.ts
// (REQUIRED_API_PREFIX).
//
// Unlike the equity / ETF / commodities domains, the crypto scorer emits
// a DOMAIN-SPECIFIC schema (no sector / category / COT): the three factor
// columns are momentum / (inverted) volatility / liquidity, with the
// composite `forge_ultra_signal` and the display-only `annualized_
// volatility`. The row shape below mirrors CRYPTO_TOP_SIGNALS_ROW_KEYS in
// app/core/cache_keys.py exactly. There is NO quick-stats / detail
// endpoint, so (unlike commodities' COT pop-over) the universe table is
// presentational only.

import { apiClient } from '../../api/client';

// Matches the exact JSON row shape from GET
// /api/v1/crypto-ranking/top-assets and /universe. Byte-identical to
// CRYPTO_TOP_SIGNALS_ROW_KEYS; keep them in lock-step.
export interface CryptoRankingRow {
    symbol: string;
    forge_ultra_signal: number;
    annualized_volatility: number;
    momentum_z_score: number;
    volatility_z_score: number;
    liquidity_z_score: number;
    universe_rank: number;
}

export interface TopAssetsResponse {
    as_of_date: string;
    top_n: number;
    rankings: CryptoRankingRow[];
}

export const fetchTopAssets = async (): Promise<TopAssetsResponse> => {
    // Resolves to GET /api/v1/crypto-ranking/top-assets.
    const { data } = await apiClient.get<TopAssetsResponse>(
        '/crypto-ranking/top-assets',
    );
    return data;
};

// --- Full universe (liquidity-screened ~18-50 coins, non-paginated) -----
// The backend returns the entire flat cross-section for the latest date in
// one payload (crypto has no category partition, so there is no
// ?category= param and the universe rows carry no `name`).

export type UniverseRow = CryptoRankingRow;

export interface UniverseResponse {
    as_of_date: string;
    total: number;
    items: UniverseRow[];
}

export const fetchUniverse = async (): Promise<UniverseResponse> => {
    // Resolves to GET /api/v1/crypto-ranking/universe.
    const { data } = await apiClient.get<UniverseResponse>(
        '/crypto-ranking/universe',
    );
    return data;
};

// --- Batched historical trajectories (sparklines) ------------------------
// One request for many symbols collapses what would be an N+1 fetch
// waterfall (one call per visible table row) into a single round-trip.

export interface HistoryPoint {
    date: string;
    forge_ultra_signal: number;
}

// Map of {symbol: [{date, forge_ultra_signal}, ...]}. Every requested
// symbol is present as a key (empty array if it has no rows in window).
export type HistoricalBatchResponse = Record<string, HistoryPoint[]>;

export const fetchHistoricalBatch = async (
    symbols: string[],
    days = 90,
): Promise<HistoricalBatchResponse> => {
    if (symbols.length === 0) return {};
    const { data } = await apiClient.get<HistoricalBatchResponse>(
        '/crypto-ranking/historical-batch',
        { params: { symbols: symbols.join(','), days } },
    );
    return data;
};

// --- Per-coin detail pop-over fetchers (CryptoDetailPopup) --------------
// Single-coin trailing-window series sourced from the new
// /crypto-ranking/{price-history,factors,signal,overview} endpoints. All
// three series endpoints are Redis-cached server-side and always return
// {symbol, count, series} (series may be empty -> render an empty-state,
// never a 404 unless the nightly chain has never run).

export interface PriceBar {
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
}

export interface PriceHistoryResponse {
    symbol: string;
    count: number;
    series: PriceBar[];
}

export const fetchPriceHistory = async (
    symbol: string,
    days = 365,
): Promise<PriceHistoryResponse> => {
    const { data } = await apiClient.get<PriceHistoryResponse>(
        `/crypto-ranking/price-history/${encodeURIComponent(symbol)}`,
        { params: { days } },
    );
    return data;
};

export interface FactorsPoint {
    date: string;
    momentum_z_score: number;
    volatility_z_score: number;
    liquidity_z_score: number;
    annualized_volatility: number;
}

export interface FactorsResponse {
    symbol: string;
    count: number;
    series: FactorsPoint[];
}

export const fetchFactors = async (
    symbol: string,
    days = 365,
): Promise<FactorsResponse> => {
    const { data } = await apiClient.get<FactorsResponse>(
        `/crypto-ranking/factors/${encodeURIComponent(symbol)}`,
        { params: { days } },
    );
    return data;
};

export interface SignalPoint {
    date: string;
    forge_ultra_signal: number;
}

export interface SignalResponse {
    symbol: string;
    count: number;
    series: SignalPoint[];
}

export const fetchSignal = async (
    symbol: string,
    days = 365,
): Promise<SignalResponse> => {
    const { data } = await apiClient.get<SignalResponse>(
        `/crypto-ranking/signal/${encodeURIComponent(symbol)}`,
        { params: { days } },
    );
    return data;
};

// One-shot latest snapshot for the popup header stats (no chart).
export interface OverviewResponse {
    symbol: string;
    as_of_date: string;
    universe_rank: number;
    forge_ultra_signal: number;
    momentum_z_score: number;
    volatility_z_score: number;
    liquidity_z_score: number;
    annualized_volatility: number;
}

export const fetchOverview = async (
    symbol: string,
): Promise<OverviewResponse> => {
    const { data } = await apiClient.get<OverviewResponse>(
        `/crypto-ranking/overview/${encodeURIComponent(symbol)}`,
    );
    return data;
};

// --- Multi-symbol compare matrices --------------------------------------
// Aligned factor + signal series for several coins (side-by-side charts).

export interface CompareSeriesPoint {
    date: string;
    forge_ultra_signal: number;
    momentum_z_score: number;
    volatility_z_score: number;
    liquidity_z_score: number;
    annualized_volatility: number;
}

export interface CompareResponse {
    as_of_date: string | null;
    days: number;
    symbols: string[];
    series: Record<string, CompareSeriesPoint[]>;
}

export const fetchCompare = async (
    symbols: string[],
    days = 365,
): Promise<CompareResponse> => {
    if (symbols.length === 0) {
        return { as_of_date: null, days, symbols: [], series: {} };
    }
    const { data } = await apiClient.get<CompareResponse>(
        '/crypto-ranking/compare',
        { params: { symbols: symbols.join(','), days } },
    );
    return data;
};

// --- Universe changelog (entrants / exits / rank moves) -----------------

export interface UniverseEntrant {
    symbol: string;
    current_rank: number;
}

export interface UniverseExit {
    symbol: string;
    prior_rank: number;
}

export interface UniverseMove {
    symbol: string;
    prior_rank: number;
    current_rank: number;
    delta: number;
}

export interface UniverseChangesResponse {
    as_of_date: string;
    prior_date: string | null;
    days: number;
    entrants: UniverseEntrant[];
    exits: UniverseExit[];
    moves: UniverseMove[];
}

export const fetchUniverseChanges = async (
    days = 7,
): Promise<UniverseChangesResponse> => {
    const { data } = await apiClient.get<UniverseChangesResponse>(
        '/crypto-ranking/universe-changes',
        { params: { days } },
    );
    return data;
};

// --- Display helper ------------------------------------------------------
// FMP quotes crypto as a concatenated USD pair with no separator
// (`BTCUSD`, `ETHUSD`); the dashboard shows the bare coin base as the
// human-readable asset name. Strips a trailing `USD` quote currency.
export const baseAsset = (symbol: string): string =>
    symbol.length > 3 && symbol.toUpperCase().endsWith('USD')
        ? symbol.slice(0, -3)
        : symbol;
